// src/pages/suppliers/components/Header.jsx
import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  TextField,
  InputAdornment,
  Avatar,
  Popover,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Chip,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  LockOutlined as LockOutlinedIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Close as CloseIcon,
  AccessTime as AccessTimeIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { BRAND_COLORS } from '../../buyers/components/styles';
import { formatDate } from '../../buyers/components/utils';

// ============================================
// HEADER COMPONENT
// ============================================

const Header = ({
  isMobile,
  onDrawerToggle,
  selectedTab,
  menuItems,
  searchTerm,
  onSearchChange,
  cartItemCount,
  onCartOpen,
  notifications,
  userData,
  onLogout,
  onChangePassword,
  onSettings,
}) => {
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Digital clock update
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleNotifOpen = (event) => setNotifAnchorEl(event.currentTarget);
  const handleNotifClose = () => setNotifAnchorEl(null);
  const handleProfileOpen = (event) => setProfileAnchorEl(event.currentTarget);
  const handleProfileClose = () => setProfileAnchorEl(null);

  const unreadCount = notifications?.filter(n => !n.read).length || 0;
  const currentTabLabel = menuItems?.find(item => item.id === selectedTab)?.label || 'Dashboard';

  // Format time
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const formatDateDisplay = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Get business name from localStorage
  const businessName = localStorage.getItem('business_name') || 
                        localStorage.getItem('supplier_name') ||
                        userData?.business_name || 
                        userData?.name || 
                        userData?.display_name ||
                        'Supplier';

  // Get verification status
  const isVerified = localStorage.getItem('business_verified') === 'true' ||
                     userData?.business_verified === true;

  const userInitial = businessName?.charAt(0)?.toUpperCase() || 'S';

  return (
    <AppBar
      position="sticky"
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(20px)',
        color: '#1A1A1A',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        borderBottom: '1px solid rgba(0,0,0,0.04)',
        transition: 'all 0.3s ease',
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
        {/* Mobile Menu Toggle */}
        {isMobile && (
          <IconButton 
            onClick={onDrawerToggle} 
            sx={{ 
              mr: 1.5,
              color: '#4B5563',
              '&:hover': {
                bgcolor: alpha(BRAND_COLORS.primary, 0.06),
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Page Title */}
        <Typography 
          variant="h6" 
          fontWeight={600} 
          sx={{ 
            flex: 1, 
            display: { xs: 'none', sm: 'block' },
            fontSize: '1.1rem',
            color: '#1A1A1A',
            letterSpacing: '0.2px',
            fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
          }}
        >
          {currentTabLabel}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Search - Desktop */}
          <TextField
            placeholder="Search products..."
            size="small"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{
              width: { xs: 140, md: 220 },
              display: { xs: 'none', sm: 'flex' },
              '& .MuiOutlinedInput-root': {
                borderRadius: 10,
                bgcolor: '#F8FAFC',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: '#F1F5F9',
                  borderColor: 'transparent',
                },
                '&.Mui-focused': {
                  bgcolor: '#FFFFFF',
                  boxShadow: `0 0 0 3px ${alpha(BRAND_COLORS.primary, 0.12)}`,
                  borderColor: BRAND_COLORS.primary,
                },
                '& fieldset': {
                  borderColor: 'transparent',
                  borderWidth: 1,
                },
                '&:hover fieldset': {
                  borderColor: 'transparent',
                },
                '&.Mui-focused fieldset': {
                  borderColor: BRAND_COLORS.primary,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton 
                    size="small" 
                    onClick={() => onSearchChange('')}
                    sx={{ 
                      color: '#9CA3AF',
                      '&:hover': { color: '#4B5563' },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Digital Clock */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 0.8,
              borderRadius: 3,
              bgcolor: alpha(BRAND_COLORS.primary, 0.04),
              border: '1px solid rgba(46, 125, 50, 0.06)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(135deg, ${alpha(BRAND_COLORS.primary, 0.02)}, transparent)`,
                pointerEvents: 'none',
              },
            }}
          >
            <AccessTimeIcon 
              sx={{ 
                fontSize: 16, 
                color: BRAND_COLORS.primary,
                animation: 'clockPulse 2s ease-in-out infinite',
              }} 
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.1 }}>
              <Typography
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: '#1A1A1A',
                  letterSpacing: '0.5px',
                  lineHeight: 1.2,
                }}
              >
                {formatTime(currentTime)}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  color: '#9CA3AF',
                  fontWeight: 500,
                  letterSpacing: '0.3px',
                  lineHeight: 1,
                }}
              >
                {formatDateDisplay(currentTime)}
              </Typography>
            </Box>
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                bgcolor: BRAND_COLORS.success,
                animation: 'statusPulse 1.5s ease-in-out infinite',
                flexShrink: 0,
              }}
            />
          </Box>

          {/* Cart */}
        

          {/* Notifications */}
          <Tooltip title="Notifications" arrow>
            <IconButton 
              onClick={handleNotifOpen}
              sx={{
                color: '#4B5563',
                '&:hover': {
                  bgcolor: alpha(BRAND_COLORS.primary, 0.06),
                  color: BRAND_COLORS.primary,
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Badge 
                badgeContent={unreadCount} 
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontWeight: 700,
                    fontSize: '0.6rem',
                    height: 18,
                    minWidth: 18,
                    borderRadius: 9,
                    boxShadow: '0 2px 8px rgba(198, 40, 40, 0.3)',
                  },
                }}
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Profile */}
          <Tooltip title="Account" arrow>
            <IconButton 
              onClick={handleProfileOpen}
              sx={{
                p: 0.5,
                border: '2px solid transparent',
                borderRadius: '50%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: alpha(BRAND_COLORS.primary, 0.3),
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryLight})`,
                  color: 'white',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  boxShadow: '0 2px 12px rgba(46, 125, 50, 0.25)',
                  transition: 'all 0.3s ease',
                }}
              >
                {userInitial}
              </Avatar>
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>

      {/* Search - Mobile */}
      {isMobile && (
        <Box sx={{ px: 2, pb: 1.5, pt: 0.5 }}>
          <TextField
            placeholder="Search products..."
            size="small"
            fullWidth
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 10,
                bgcolor: '#F8FAFC',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: '#F1F5F9',
                },
                '&.Mui-focused': {
                  bgcolor: '#FFFFFF',
                  boxShadow: `0 0 0 3px ${alpha(BRAND_COLORS.primary, 0.12)}`,
                },
                '& fieldset': {
                  borderColor: 'transparent',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: '#9CA3AF' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {/* Notifications Popover */}
      <Popover
        open={Boolean(notifAnchorEl)}
        anchorEl={notifAnchorEl}
        onClose={handleNotifClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 420,
            borderRadius: 3,
            boxShadow: '0 8px 40px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)',
            border: '1px solid rgba(0,0,0,0.04)',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ 
          p: 2, 
          borderBottom: '1px solid #E5E7EB',
          bgcolor: '#F8FAFC',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Chip
              label={`${unreadCount} new`}
              size="small"
              sx={{ 
                bgcolor: BRAND_COLORS.error, 
                color: 'white',
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 22,
              }}
            />
          )}
        </Box>
        <Box sx={{ overflow: 'auto' }}>
          {notifications?.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: '#F3F4F6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5,
                }}
              >
                <NotificationsIcon sx={{ color: '#9CA3AF' }} />
              </Box>
              <Typography variant="body2" color="textSecondary" fontWeight={500}>
                No notifications
              </Typography>
              <Typography variant="caption" color="textSecondary">
                You're all caught up!
              </Typography>
            </Box>
          ) : (
            notifications.map((notif) => (
              <Box
                key={notif.id}
                sx={{
                  p: 2,
                  borderBottom: '1px solid #F3F4F6',
                  bgcolor: notif.read ? 'transparent' : alpha(BRAND_COLORS.primary, 0.02),
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: '#F8FAFC' },
                  cursor: 'default',
                  position: 'relative',
                }}
              >
                {!notif.read && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: BRAND_COLORS.primary,
                    }}
                  />
                )}
                <Typography
                  variant="body2"
                  fontWeight={notif.read ? 400 : 600}
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    pr: 2,
                  }}
                >
                  {notif.message}
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                  {formatDate(notif.createdAt)}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      </Popover>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchorEl}
        open={Boolean(profileAnchorEl)}
        onClose={handleProfileClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 260,
            borderRadius: 3,
            boxShadow: '0 8px 40px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)',
            border: '1px solid rgba(0,0,0,0.04)',
            overflow: 'hidden',
            mt: 0.5,
          },
        }}
      >
        <Box sx={{ 
          px: 2.5, 
          py: 2,
          background: `linear-gradient(135deg, ${alpha(BRAND_COLORS.primary, 0.04)}, ${alpha(BRAND_COLORS.primaryLight, 0.04)})`,
          borderBottom: '1px solid #E5E7EB',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                width: 44,
                height: 44,
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryLight})`,
                color: 'white',
                fontWeight: 700,
                fontSize: '1rem',
                boxShadow: '0 2px 12px rgba(46, 125, 50, 0.25)',
              }}
            >
              {userInitial}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {businessName}
                </Typography>
                {isVerified && (
                  <VerifiedIcon 
                    sx={{ 
                      fontSize: 16, 
                      color: BRAND_COLORS.success,
                    }} 
                  />
                )}
              </Box>
              <Typography variant="caption" color="textSecondary" noWrap>
                {userData?.email || ''}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
            <Chip
              label={isVerified ? 'Verified Supplier' : 'Pending Verification'}
              size="small"
              sx={{
                bgcolor: isVerified ? alpha(BRAND_COLORS.success, 0.12) : alpha(BRAND_COLORS.warning, 0.12),
                color: isVerified ? BRAND_COLORS.success : BRAND_COLORS.warning,
                fontWeight: 600,
                fontSize: '0.6rem',
                height: 22,
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
            <Chip
              label="Active"
              size="small"
              sx={{
                bgcolor: alpha(BRAND_COLORS.success, 0.12),
                color: BRAND_COLORS.success,
                fontWeight: 600,
                fontSize: '0.6rem',
                height: 22,
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
          </Box>
        </Box>
        
       
        
     
       
        <Divider sx={{ my: 0.5 }} />
        
        <MenuItem 
          onClick={onLogout} 
          sx={{
            py: 1.2,
            px: 2.5,
            gap: 2,
            color: BRAND_COLORS.error,
            '&:hover': {
              bgcolor: alpha(BRAND_COLORS.error, 0.06),
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32, color: BRAND_COLORS.error }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" fontWeight={600}>Logout</Typography>
        </MenuItem>
      </Menu>

      {/* Global Styles for Animations */}
      <style>
        {`
          @keyframes statusPulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.3;
              transform: scale(0.6);
            }
          }
          @keyframes clockPulse {
            0%, 100% {
              opacity: 0.8;
              transform: scale(1);
            }
            50% {
              opacity: 1;
              transform: scale(1.1);
            }
          }
        `}
      </style>
    </AppBar>
  );
};

export default Header;